import type { FieldDefinition } from '@cms/contracts'
import { summaryOf } from '../domain/humane.js'
import { useI18n } from '../i18n/context.js'
import { Button, IconButton } from '../ui/primitives.js'
import { FieldControl } from './FieldControl.js'
import { GroupBox } from './ValueEditor.js'

interface ListFieldControlProps {
  itemFields: readonly FieldDefinition[]
  value: unknown[]
  onChange: (value: unknown[]) => void
  disabled?: boolean
}

/** A list of objects with add, remove and reorder: each row opens into a small form built from `field.of`. */
export function ListFieldControl({ itemFields, value, onChange, disabled }: ListFieldControlProps) {
  const { t } = useI18n()

  const emptyItem = (): Record<string, unknown> =>
    Object.fromEntries(itemFields.map((f) => [f.key, f.type === 'boolean' ? false : f.type === 'list' ? [] : '']))

  const updateItem = (index: number, next: Record<string, unknown>) => {
    onChange(value.map((item, i) => (i === index ? next : item)))
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= value.length) return
    const next = [...value]
    const a = next[index]
    const b = next[target]
    if (a === undefined || b === undefined) return
    next[index] = b
    next[target] = a
    onChange(next)
  }

  return <div className="list-control">
    {value.length === 0 && <p className="muted">{t('editor.noItems')}</p>}
    {value.map((rawItem, index) => {
      const item = (rawItem && typeof rawItem === 'object' ? rawItem : {}) as Record<string, unknown>
      const name = summaryOf(item)
      return <GroupBox
        key={index}
        depth={2}
        defaultOpen={value.length <= 3}
        label={name || t('editor.itemNumber', { number: index + 1 })}
        hint={name ? t('editor.itemNumber', { number: index + 1 }) : undefined}
        actions={!disabled ? <>
          <IconButton icon="arrow_upward" label={t('editor.moveUp')} onClick={() => moveItem(index, -1)} disabled={index === 0}/>
          <IconButton icon="arrow_downward" label={t('editor.moveDown')} onClick={() => moveItem(index, 1)} disabled={index === value.length - 1}/>
          <IconButton icon="delete" label={t('editor.removeItem')} onClick={() => onChange(value.filter((_, i) => i !== index))}/>
        </> : undefined}
      >
        <div className="list-item-fields">
          {itemFields.map((subField) => <FieldControl
            key={subField.key}
            field={subField}
            value={item[subField.key]}
            onChange={(next) => updateItem(index, { ...item, [subField.key]: next })}
            disabled={disabled}
          />)}
        </div>
      </GroupBox>
    })}
    {!disabled && <div><Button variant="tonal" small icon="add" onClick={() => onChange([...value, emptyItem()])}>{t('editor.addItem')}</Button></div>}
  </div>
}
